import { useLingui } from '@lingui/react';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/ui/status-badge';
import type { WorkflowItemResponse } from '@/types/workflow';
import { getTriageBucketLabel, type TriageBucket } from '@/types/changeRequest';
import { WorkflowBoardCard } from './WorkflowBoardCard';
import type { PreviewLine } from './approvalPreviewText';
import { batchLockReason, isInBatch } from './useBatchApproveFlow';

type Item = WorkflowItemResponse & { id: string };

interface ChangeRequestTriageGroupProps {
  bucket: TriageBucket;
  items: Item[];
  onOpenDetail: (itemId: string) => void;
  onActionClick: (itemId: string, action: string, currentState: string) => void;
  /** When provided (the Routine bucket), renders "Approve all" for the batchable items. */
  onBatchApprove?: (items: Item[]) => void;
  batchPending?: boolean;
  /** Requests the last "Approve all" check held back, with their first blocker. */
  heldBack?: ReadonlyMap<string, PreviewLine | null>;
}

const NO_HELD_BACK: ReadonlyMap<string, PreviewLine | null> = new Map();

/**
 * One triage bucket ("Needs your review" / "Routine") with its cards. The
 * Routine bucket carries "Approve all", which checks each request's approval
 * preview before anything is approved. A card that cannot join the batch shows
 * a lock with the reason.
 */
export function ChangeRequestTriageGroup({
  bucket,
  items,
  onOpenDetail,
  onActionClick,
  onBatchApprove,
  batchPending,
  heldBack = NO_HELD_BACK,
}: ChangeRequestTriageGroupProps) {
  const { i18n } = useLingui();

  // Only pending items whose FSM still offers `approve` are batch-approvable —
  // never submit a row the API has already removed the approve transition from.
  // Kinds that need a person (termination, new contact, group move) and items a
  // blocker held back are excluded even if they reach this bucket.
  const approvable = items.filter((it) => isInBatch(it, heldBack));

  return (
    <div className="flex min-h-0 min-w-0 flex-col" data-testid={`triage-bucket-${bucket}`}>
      <div className="flex items-center justify-between mb-3 px-1 gap-2">
        <div className="flex items-center gap-2">
          <StatusBadge
            tone={bucket === 'needs_handholding' ? 'warning' : 'success'}
            label={getTriageBucketLabel(i18n, bucket)}
          />
          <span className="text-xs text-muted-foreground font-medium">{items.length}</span>
        </div>
        {onBatchApprove && approvable.length > 0 && (
          <Button
            size="sm"
            variant="default"
            className="h-7 text-xs"
            disabled={batchPending}
            onClick={() => onBatchApprove(approvable)}
            data-testid={`triage-batch-approve-${bucket}`}
          >
            {i18n._({ id: 'workflow.triage.approveAllCount', message: 'Approve all ({count})', values: { count: approvable.length } })}
          </Button>
        )}
      </div>
      {bucket === 'needs_handholding' && (
        <p className="mb-2 px-1 text-xs text-muted-foreground" data-testid="triage-bucket-hint-needs_handholding">
          {i18n._({
            id: 'workflow.triage.needsHandholding.hint',
            message:
              'Review these one by one. Terminations, pick-up or emergency contact changes and group moves always land here because they change who may take the child, where the child is, or end the contract.',
          })}
        </p>
      )}

      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-lg bg-muted/30 p-2">
        {items.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-xs text-muted-foreground">
            {i18n._({ id: 'workflow.board.empty', message: 'No items' })}
          </div>
        ) : (
          items.map((item) => (
            <WorkflowBoardCard
              key={item.id}
              item={item}
              onOpenDetail={onOpenDetail}
              onActionClick={onActionClick}
              lockReason={batchLockReason(i18n, item, heldBack)}
            />
          ))
        )}
      </div>
    </div>
  );
}
